import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import orphanTransactionProvider, {
	type CreditOrphanToMerchantRequest,
	type LinkOrphanToOrderRequest,
	type LinkOrphanToPaymentRequest,
	type ListOrphanTransactionsParams,
	type OrphanCreditEstimate,
	type OrphanTransactionDetail,
	type PaginatedOrphanTransactions,
} from "src/providers/orphan-transaction-provider";
import userProvider, {
	type PaginatedMerchantsResponse,
} from "src/providers/user-provider";

export function useMerchantSearch(query: string) {
	const trimmed = query.trim();
	return useQuery<PaginatedMerchantsResponse>({
		queryKey: ["admin-merchant-search", trimmed],
		queryFn: () =>
			userProvider.listMerchants({ search: trimmed, limit: 10, offset: 0 }),
		staleTime: 30_000,
	});
}

export function useOrphanTransactions(
	params: ListOrphanTransactionsParams = {},
) {
	return useQuery<PaginatedOrphanTransactions>({
		queryKey: ["orphan-transactions", params],
		queryFn: () => orphanTransactionProvider.list(params),
		staleTime: 30_000,
		refetchInterval: 60_000,
	});
}

export function useOrphanTransaction(id: number | null, enabled = true) {
	return useQuery<OrphanTransactionDetail>({
		queryKey: ["orphan-transaction", id],
		queryFn: () => {
			if (id === null) {
				return Promise.reject(new Error("Orphan transaction ID is required"));
			}
			return orphanTransactionProvider.get(id);
		},
		enabled: id !== null && enabled,
		staleTime: 30_000,
		// Once assigned, the tx stops being an orphan and this endpoint 404s.
		// Don't retry it into a spurious error toast, and don't let a window
		// refocus resurrect a fetch for a deposit that's already been handled.
		retry: false,
		refetchOnWindowFocus: false,
	});
}

// After a successful assignment the transaction is no longer an orphan, so its
// detail endpoint would 404. We never re-read it (the caller disables the
// detail query and shows a captured result), so here we only refresh the list
// it has now dropped out of. Shared by all three assignment mutations.
function settleAssignedOrphan(queryClient: ReturnType<typeof useQueryClient>) {
	queryClient.invalidateQueries({ queryKey: ["orphan-transactions"] });
}

export function useLinkOrphanToOrder() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: number;
			data: LinkOrphanToOrderRequest;
		}) => orphanTransactionProvider.linkToOrder(id, data),
		onSuccess: () => settleAssignedOrphan(queryClient),
	});
}

export function useLinkOrphanToPayment() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: number;
			data: LinkOrphanToPaymentRequest;
		}) => orphanTransactionProvider.linkToPayment(id, data),
		onSuccess: () => settleAssignedOrphan(queryClient),
	});
}

// Keyed on (orphanId, merchantUuid) so the cache automatically refreshes when
// the operator types a different UUID. Server validates the UUID — we just
// skip the call on empty input.
export function useOrphanCreditEstimate(
	orphanId: number | null,
	merchantUuid: string,
) {
	const trimmed = merchantUuid.trim();
	return useQuery<OrphanCreditEstimate>({
		queryKey: ["orphan-estimate", orphanId, trimmed],
		queryFn: () => {
			if (orphanId === null || !trimmed) {
				return Promise.reject(
					new Error("orphan id and merchant uuid required"),
				);
			}
			return orphanTransactionProvider.estimateForMerchant(orphanId, trimmed);
		},
		enabled: orphanId !== null && trimmed.length > 0,
		retry: false,
		staleTime: 10_000,
	});
}

export function useCreditOrphanToMerchant() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: number;
			data: CreditOrphanToMerchantRequest;
		}) => orphanTransactionProvider.creditToMerchant(id, data),
		onSuccess: () => settleAssignedOrphan(queryClient),
	});
}
